import type { BadgeVariant } from '@/components/ui';
import type {
  AcknowledgementMode,
  PolicyAssignmentStatus,
  PolicyStatus,
  PolicyVersionStatus,
} from '@/api/services/hrPolicies.service';

export const POLICY_VERSION_STATUS: Record<
  PolicyVersionStatus,
  { label: string; variant: BadgeVariant }
> = {
  draft: { label: 'Draft', variant: 'neutral' },
  pending_approval: { label: 'Awaiting approval', variant: 'warning' },
  approved: { label: 'Approved, not published', variant: 'info' },
  scheduled: { label: 'Scheduled', variant: 'info' },
  published: { label: 'Published', variant: 'success' },
  superseded: { label: 'Superseded', variant: 'neutral' },
  withdrawn: { label: 'Withdrawn', variant: 'neutral' },
};

export const POLICY_STATUS: Record<PolicyStatus, { label: string; variant: BadgeVariant }> = {
  active: { label: 'Active', variant: 'success' },
  withdrawn: { label: 'Withdrawn', variant: 'neutral' },
  archived: { label: 'Archived', variant: 'neutral' },
};

export const ASSIGNMENT_STATUS: Record<
  PolicyAssignmentStatus,
  { label: string; variant: BadgeVariant }
> = {
  pending: { label: 'To acknowledge', variant: 'warning' },
  acknowledged: { label: 'Acknowledged', variant: 'success' },
  carried_forward: { label: 'Covered by earlier version', variant: 'success' },
  not_required: { label: 'For reading', variant: 'neutral' },
  exempted: { label: 'Exempted', variant: 'neutral' },
  removed: { label: 'No longer applies', variant: 'neutral' },
  superseded: { label: 'Replaced', variant: 'neutral' },
  withdrawn: { label: 'Withdrawn', variant: 'neutral' },
};

export const ACKNOWLEDGEMENT_MODE_LABELS: Record<AcknowledgementMode, string> = {
  none: 'Read only, no acknowledgement',
  click: 'Acknowledge with a confirmation',
  drawn: 'Acknowledge with a drawn signature',
};

/** The date shown on the policy PDF and used for its audience deadlines. */
export function toDateInput(value: string | undefined, timeZone = 'UTC'): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function formatPolicyDate(value: string | undefined | null, timeZone = 'UTC'): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/** Start of the chosen day in the policy timezone, or now when it is today. */
export function fromDateInput(value: string, timeZone = 'UTC'): string {
  const [year, month, day] = value.split('-').map(Number);
  const today = new Date();
  if (value === toDateInput(today.toISOString(), timeZone)) return today.toISOString();

  const target = Date.UTC(year, month - 1, day);
  let instant = target;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(instant));
    const part = (type: string) => Number(parts.find((item) => item.type === type)?.value);
    const local = Date.UTC(
      part('year'),
      part('month') - 1,
      part('day'),
      part('hour'),
      part('minute'),
      part('second')
    );
    instant += target - local;
  }
  return new Date(instant).toISOString();
}

export function completionPercent(complete: number, audience: number): number {
  return audience === 0 ? 0 : Math.round((complete / audience) * 100);
}
